import type {
  CalendarAttachment,
  CalendarVisit,
  Event,
  VisitPosition
} from "../../types/types.ts"

export const hasVisitData = (event: Event): boolean =>
  !!event?.visit || !!event?.visits?.length

// The ids the visit section will actually render: the caller's own visit plus
// every visit the server chose to send. Anything outside this set has no
// surface of its own, so its files have to stay in the general block.
export const getRenderedVisitIds = (event: Event): Set<string> => {
  const ids = new Set<string>()
  if (event?.visit) ids.add(event.visit.id)
  for (const visit of event?.visits ?? []) ids.add(visit.id)
  return ids
}

export const getVisitAttachments = (
  attachments: CalendarAttachment[] | undefined,
  visitId: string
): CalendarAttachment[] =>
  (attachments ?? []).filter((attachment) => attachment.visit === visitId)

export const getEventAttachments = (
  attachments: CalendarAttachment[] | undefined,
  renderedVisitIds: Set<string>
): CalendarAttachment[] =>
  (attachments ?? []).filter(
    (attachment) => !attachment.visit || !renderedVisitIds.has(attachment.visit)
  )

export const getOtherVisits = (
  visits: CalendarVisit[] | undefined,
  ownVisitId: string | undefined
): CalendarVisit[] => (visits ?? []).filter((visit) => visit.id !== ownVisitId)

// `typeof` rather than a truthiness check: latitude 0 is a real coordinate.
// Narrowing once here is what keeps the two readers below cast-free.
export const toVisitPosition = (
  lat: number | null,
  lng: number | null,
  accuracy: number | null
): VisitPosition | null =>
  typeof lat === "number" && typeof lng === "number"
    ? { lat, lng, accuracy }
    : null

export const formatVisitCoordinates = (position: VisitPosition): string =>
  `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`

// Hours and minutes are separate i18n keys because inflection differs.
export const splitDuration = (
  totalMinutes: number
): { hours: number; minutes: number } => ({
  hours: Math.floor(totalMinutes / 60),
  minutes: totalMinutes % 60
})

export const buildVisitMapUrl = (position: VisitPosition): string =>
  `https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}`

// Same URL pattern, keyed on the address text instead of coordinates: the
// event's address is free text a user can edit, so a text query can never
// strand stale coordinates the way a lat/lng copy would.
export const buildAddressMapUrl = (address: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
